import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import VitalManualEntryLayout from "../../components/Common/VitalManualEntryLayout";
import {
  BloodPressureInput,
  getVitalInputError,
} from "../../components/Common/VitalInput";
import { submitHealthProfileEntry } from "../../utils/submitHealthProfileEntry";
import { useSubmitVitalForm } from "../../hooks/useSubmitVitalForm";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";

const SYSTOLIC_MIN = 50;
const SYSTOLIC_MAX = 250;
const DIASTOLIC_MIN = 30;
const DIASTOLIC_MAX = 170;

export interface BloodPressureManualEntryProps {
  onBack?: () => void;
  onCancel?: () => void;
  /** Called after save succeeds; may be async (e.g. refetch). Metric screen shows loading until this completes. */
  onSave?: () => void | Promise<void>;
}

export default function BloodPressureManualEntry({
  onBack,
  onCancel,
  onSave,
}: BloodPressureManualEntryProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [systolic, setSystolic] = useState("");
  const [diastolic, setDiastolic] = useState("");

  const handleBack = onBack ?? (() => navigate(-1));
  const handleCancel = onCancel ?? (() => navigate(-1));

  const getSubmitPromise = useCallback(
    () =>
      Promise.all([
        submitHealthProfileEntry("SystolicBloodPressure", systolic),
        submitHealthProfileEntry("DiastolicBloodPressure", diastolic),
      ]),
    [systolic, diastolic]
  );

  const hasSystolicError = getVitalInputError(
    systolic,
    SYSTOLIC_MIN,
    SYSTOLIC_MAX
  );
  const hasDiastolicError = getVitalInputError(
    diastolic,
    DIASTOLIC_MIN,
    DIASTOLIC_MAX
  );
  const hasIncompleteBP =
    !systolic || !diastolic || systolic === "-" || diastolic === "-";

  const { handleSave, isSubmitting } = useSubmitVitalForm({
    getSubmitPromise,
    canSubmit: !hasIncompleteBP && !hasSystolicError && !hasDiastolicError,
    errorMessage: t(
      "Metric.manualEntry.updateError",
      "Unable to save. Please try again."
    ),
    onSuccess: onSave,
  });

  if (isSubmitting) {
    return <FullscreenLoadingIndicator mainIconType="face" />;
  }

  return (
    <VitalManualEntryLayout
      title={t("BloodPressure.manualEntry.title")}
      onBack={handleBack}
      onCancel={handleCancel}
      onSave={handleSave}
      isSaveDisabled={
        hasIncompleteBP || isSubmitting || hasSystolicError || hasDiastolicError
      }
      cancelText={t("BloodPressure.manualEntry.cancel")}
      saveText={t("BloodPressure.manualEntry.save")}
    >
      <BloodPressureInput
        systolic={systolic}
        diastolic={diastolic}
        onSystolicChange={setSystolic}
        onDiastolicChange={setDiastolic}
        systolicLabel={t("BloodPressure.manualEntry.systolicLabel")}
        diastolicLabel={t("BloodPressure.manualEntry.diastolicLabel")}
        unit={t("BloodPressure.unit")}
        systolicMin={SYSTOLIC_MIN}
        systolicMax={SYSTOLIC_MAX}
        diastolicMin={DIASTOLIC_MIN}
        diastolicMax={DIASTOLIC_MAX}
        integerOnly
        systolicErrorText={t(
          "BloodPressure.manualEntry.systolicValidationError",
          "Systolic must be between 50 and 250 mmHg"
        )}
        diastolicErrorText={t(
          "BloodPressure.manualEntry.diastolicValidationError",
          "Diastolic must be between 30 and 170 mmHg"
        )}
      />
    </VitalManualEntryLayout>
  );
}
